import { useState } from 'react';
import type { CurvePoint } from '@chemai/engine';
import { useI18n } from '../i18n';

interface Props {
  maxVolume: number;
  theory: CurvePoint[] | null;
  recorded: CurvePoint[];
  current: CurvePoint;
  equivalence: number | null;
  indicatorRange?: [number, number];
}

const W = 600;
const H = 340;
const M = { l: 48, r: 16, t: 16, b: 44 };

/** Accessible SVG titration curve with optional data table. */
export function PhChart({ maxVolume, theory, recorded, current, equivalence, indicatorRange }: Props) {
  const { t, num } = useI18n();
  const [table, setTable] = useState(false);
  const x = (v: number) => M.l + (v / maxVolume) * (W - M.l - M.r);
  const y = (pH: number) => M.t + (1 - pH / 14) * (H - M.t - M.b);
  const xticks = Array.from({ length: 6 }, (_, i) => (maxVolume * i) / 5);
  const path = (pts: CurvePoint[]) => pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.v).toFixed(1)},${y(p.pH).toFixed(1)}`).join('');

  return (
    <figure className="chart">
      <figcaption className="chart-title">{t('tit.chart')}</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${t('tit.chart')}: ${t('tit.added')} ${num(current.v)} mL, pH ${num(current.pH)}`}>
        {indicatorRange && (
          <rect x={M.l} width={W - M.l - M.r} y={y(indicatorRange[1])} height={y(indicatorRange[0]) - y(indicatorRange[1])} className="chart-band" />
        )}
        {[0, 2, 4, 6, 7, 8, 10, 12, 14].map((p) => (
          <g key={p}>
            <line x1={M.l} x2={W - M.r} y1={y(p)} y2={y(p)} className={p === 7 ? 'grid grid-7' : 'grid'} />
            <text x={M.l - 8} y={y(p) + 4} className="tick" textAnchor="end">{p}</text>
          </g>
        ))}
        {xticks.map((v) => (
          <text key={v} x={x(v)} y={H - M.b + 18} className="tick" textAnchor="middle">{num(v, 1)}</text>
        ))}
        <text x={(W + M.l) / 2} y={H - 6} className="axis-label" textAnchor="middle">{t('tit.axisV')}</text>
        <text x={14} y={(H - M.b) / 2} className="axis-label" textAnchor="middle" transform={`rotate(-90 14 ${(H - M.b) / 2})`}>{t('tit.axisPH')}</text>
        {equivalence !== null && equivalence <= maxVolume && (
          <line x1={x(equivalence)} x2={x(equivalence)} y1={M.t} y2={H - M.b} className="eq-line" />
        )}
        {theory && <path d={path(theory)} className="curve-theory" />}
        {recorded.length > 1 && <path d={path(recorded)} className="curve-measured" />}
        {recorded.map((p, i) => <circle key={i} cx={x(p.v)} cy={y(p.pH)} r={2.6} className="pt" />)}
        <circle cx={x(current.v)} cy={y(current.pH)} r={6} className="pt-current" />
      </svg>
      <button type="button" className="btn-link" onClick={() => setTable((s) => !s)} aria-expanded={table}>
        {table ? t('common.hideTable') : t('common.showTable')}
      </button>
      {table && (
        <div className="table-wrap">
          <table>
            <thead><tr><th scope="col">{t('tit.axisV')}</th><th scope="col">pH</th></tr></thead>
            <tbody>
              {recorded.map((p, i) => <tr key={i}><td>{num(p.v, 2)}</td><td>{num(p.pH, 2)}</td></tr>)}
            </tbody>
          </table>
        </div>
      )}
    </figure>
  );
}
